import { ObjectDataStore } from "Stores/RESTStoreInstances/ObjectDataStore";
// import { API_URL } from 'General/APIEndpoints';
// import { projectsDataStore } from 'Stores';
import { action, computed, observable, runInAction, makeObservable } from "mobx";
import { qaCategoriesStore } from "./QACategoriesStore";
import { API } from "General/API";
import { API_URL } from "General/APIEndpoints";
import { projectsDataStore } from "Stores";
import { handleStoreState } from "Stores/RESTStore/CoreStore";
import { SuggestionsConfig } from "../Components/AddCommentForm";
import { qaUnreadStore } from "./QAUnreadStore";
import { formatUsername } from "utils/formatUsername";

export class QADetailsStore extends ObjectDataStore<API_Question> {
  constructor() {
    super();

    makeObservable(this, {
      questionId: observable,
      suggestions: observable,
      suggestionsState: observable,
      relatedQuestionTopic: observable,
      setQuestionId: action.bound,
      setData: action.bound,
      changeCategory: action.bound,
      changeType: action.bound,
      fetchRelatedQuestionTopic: action.bound,
      category: computed,
      delete: action.bound,
      startWork: action.bound,
      forwardToSpecialist: action.bound,
      submit: action.bound,
      admit: action.bound,
      rejectQuestion: action.bound,
      fetchSuggestions: action.bound,
      changeDelegated: action.bound,
    });
  }
  questionId: number = null;
  suggestions: SuggestionsConfig = [];
  suggestionsState: StoreState = "RAW";
  relatedQuestionTopic = "";
  private role: API_QA_ROLE;

  setQuestionId(id: number) {
    this.questionId = id;
  }

  clear() {
    super.clear();
    this.relatedQuestionTopic = "";
    this.suggestions = [];
    this.suggestionsState = "RAW";
    this.questionId = undefined;
    this.role = undefined;
  }

  url() {
    return API_URL.QA.getQuestion(projectsDataStore.projectId, this.questionId);
  }

  @handleStoreState()
  async fetch({ role }: { role?: API_QA_ROLE } = {}) {
    try {
      if (role) {
        this.role = role;
      }
      const response = await API.getData<API_Question>(this.url(), { role: this.role });
      runInAction(() => {
        this.data = response;
      });
      if (response.parentQuestionId) {
        await this.fetchRelatedQuestionTopic(response.parentQuestionId);
      } else {
        runInAction(() => {
          this.relatedQuestionTopic = "";
        });
      }
    } catch (error) {
      throw error;
    }
  }

  setData(question: API_Question) {
    this.data = question;
  }

  @handleStoreState()
  async delete() {
    try {
      await API.editData(API_URL.QA.withdraw(projectsDataStore.projectId, this.questionId), {});
    } catch (error) {
      throw error;
    }
  }

  async changeCategory(categoryId: number) {
    try {
      await API.editData(
        API_URL.QA.changeCategory(projectsDataStore.projectId, this.questionId, categoryId),
        {},
        { role: this.role }
      );
      runInAction(() => {
        this.data.categoryId = categoryId;
      });
    } catch (error) {
      throw error;
    }
  }

  async changeType(isPrivate: boolean) {
    try {
      const params: { type: API_QA_TYPE } = { type: isPrivate ? "PRIVATE" : "PUBLIC" };
      await API.editData(API_URL.QA.publish(projectsDataStore.projectId, this.questionId), {}, params);
      runInAction(() => {
        this.data.private = isPrivate;
      });
    } catch (error) {
      throw error;
    }
  }

  @handleStoreState()
  async startWork() {
    try {
      const response: API_Question = await API.editData(
        API_URL.QA.startWork(projectsDataStore.projectId, this.questionId),
        {},
        { role: this.role }
      );
      runInAction(() => {
        this.update(response);
      });
    } catch (error) {
      throw error;
    }
  }

  @handleStoreState()
  async forwardToSpecialist() {
    try {
      const response: API_Question = await API.editData(
        API_URL.QA.forwardToSpecialist(projectsDataStore.projectId, this.questionId),
        {},
        { role: this.role }
      );
      runInAction(() => {
        this.update(response);
      });
      qaUnreadStore.fetch();
    } catch (error) {
      throw error;
    }
  }

  @handleStoreState()
  async submit() {
    try {
      const response = await API.editData(
        API_URL.QA.submit(projectsDataStore.projectId, this.questionId),
        {},
        { role: this.role }
      );
      runInAction(() => {
        this.update(response);
      });
    } catch (error) {
      throw error;
    }
  }

  @handleStoreState()
  async admit() {
    try {
      const response = await API.editData(
        API_URL.QA.admit(projectsDataStore.projectId, this.questionId),
        {},
        { role: this.role }
      );
      runInAction(() => {
        this.update(response);
      });
    } catch (error) {
      throw error;
    }
  }

  @handleStoreState()
  async rejectQuestion(answer: string, params: { role: API_QA_ROLE }) {
    try {
      let urlFn: (...args: number[]) => string;
      switch (params.role) {
        case "GATEKEEPER_BUYER":
          urlFn = API_URL.QA.buyerGatekeeperReject;
          break;
        case "GATEKEEPER_SELLER":
          urlFn = API_URL.QA.sellerGatekeeperReject;
          break;
        case "SPECIALIST":
          urlFn = API_URL.QA.specialistReject;
          break;
        default:
          throw new Error("Unhandled case with role: " + params.role);
      }
      const response: API_Question = await API.newData(
        urlFn(projectsDataStore.projectId, this.questionId),
        answer,
        params
      );
      runInAction(() => {
        this.update(response);
      });
      qaUnreadStore.fetch();
    } catch (error) {
      throw error;
    }
  }

  @handleStoreState<QADetailsStore>("suggestionsState")
  async fetchSuggestions() {
    try {
      const users: API_User[] = await API.getData(API_URL.QA.getMentionedUsers(projectsDataStore.projectId));
      runInAction(() => {
        this.suggestions = users.map((item) => ({
          label: formatUsername({ firstName: item.firstName, surname: item.surname }),
          value: item.id.toString(),
        }));
      });
    } catch (error) {
      throw error;
    }
  }

  async changeDelegated(userId: number) {
    const response = await API.editData<API_Question>(
      API_URL.QA.delegate(projectsDataStore.projectId, this.questionId),
      null,
      { delegateTo: userId }
    );
    runInAction(() => {
      this.data = response;
    });
  }

  async fetchRelatedQuestionTopic(_questionId: number) {
    const { topic } = await API.getData<API_Question>(
      API_URL.QA.getQuestion(projectsDataStore.projectId, this.data.parentQuestionId)
    );
    runInAction(() => {
      this.relatedQuestionTopic = topic;
    });
  }

  get category() {
    if (this.data.categoryId) {
      return qaCategoriesStore.find(this.data.categoryId, "id");
    } else {
      return null;
    }
  }

  /**
   * return base form data because back-end needs to get some other fields that are
   * not even changed within request...
   */
  getEditFormData(): API_Question {
    return {
      priority: this.data.priority,
      topic: this.data.topic,
      question: this.data.question,
    };
  }
}

export const qaDetailsStore = new QADetailsStore();
